import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Loader2, RefreshCw, Copy, ShieldAlert, ShieldCheck, Users, KeyRound, UserPlus } from "lucide-react";
import PermissionsPanel from "@/auth/PermissionsPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPanel() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(null);
  const [custom, setCustom] = useState({});
  const [newUser, setNewUser] = useState({ email: "", name: "", password: "" });
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/users`);
      setUsers(data);
    } catch (e) {
      toast.error("No se pudo cargar la lista de agentes");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const createUser = useCallback(async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = { email: newUser.email.trim(), name: newUser.name.trim() || undefined };
      if (newUser.password.trim()) payload.password = newUser.password.trim();
      const { data } = await axios.post(`${API}/admin/users`, payload);
      toast.success(`Agente creado. Contraseña: ${data.temp_password}`, { duration: 15000 });
      setNewUser({ email: "", name: "", password: "" });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo crear el agente");
    } finally {
      setCreating(false);
    }
  }, [newUser, load]);

  const reset = useCallback(async (u, password) => {
    const msg = password
      ? `¿Fijar la contraseña "${password}" para ${u.email}?`
      : `¿Generar una nueva contraseña temporal para ${u.email}?`;
    if (!window.confirm(msg)) return;
    setResetting(u.id);
    try {
      const { data } = await axios.post(`${API}/admin/users/${u.id}/reset-password`, password ? { password } : {});
      toast.success(`Nueva contraseña: ${data.temp_password}`, { duration: 12000 });
      setCustom((c) => ({ ...c, [u.id]: "" }));
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo resetear la contraseña");
    } finally {
      setResetting(null);
    }
  }, [load]);

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success("Copiado al portapapeles");
  };

  return (
    <div className="space-y-6" data-testid="admin-area">
    <PermissionsPanel users={users} loading={loading} onChanged={load} />
    <Card className="shadow-none border-border" data-testid="admin-panel">
      <CardHeader className="border-b border-border pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            <h2 className="font-heading text-base font-bold tracking-tight">Gestión de agentes</h2>
          </div>
          <Button
            data-testid="admin-reload-button"
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
            className="rounded-md"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Contraseñas temporales para los agentes. Puedes generar una aleatoria o fijar una personalizada (mín. 8 caracteres). Cuando un agente cambie la suya, la temporal desaparecerá.
        </p>
      </CardHeader>
      <CardContent className="pt-0 divide-y divide-border">
        <form
          data-testid="admin-create-user-form"
          onSubmit={createUser}
          className="flex flex-wrap items-end gap-2 pb-4"
        >
          <div className="flex flex-col gap-1">
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Nuevo agente</label>
            <Input
              data-testid="admin-new-email-input"
              type="email"
              required
              value={newUser.email}
              onChange={(e) => setNewUser((n) => ({ ...n, email: e.target.value }))}
              placeholder="nombre@viajadverdad.com"
              className="h-8 w-64 rounded-md text-xs"
            />
          </div>
          <Input
            data-testid="admin-new-name-input"
            value={newUser.name}
            onChange={(e) => setNewUser((n) => ({ ...n, name: e.target.value }))}
            placeholder="Nombre (opcional)"
            className="h-8 w-40 rounded-md text-xs"
          />
          <Input
            data-testid="admin-new-password-input"
            value={newUser.password}
            onChange={(e) => setNewUser((n) => ({ ...n, password: e.target.value }))}
            placeholder="Contraseña (opcional, mín. 8)"
            className="h-8 w-56 rounded-md text-xs font-mono"
            autoComplete="off"
          />
          <Button
            data-testid="admin-create-user-button"
            type="submit"
            size="sm"
            disabled={creating || !newUser.email || (newUser.password.trim().length > 0 && newUser.password.trim().length < 8)}
            className="rounded-md"
          >
            {creating ? <Loader2 className="h-3 w-3 animate-spin" /> : <span className="flex items-center gap-1"><UserPlus className="h-3 w-3" /> crear agente</span>}
          </Button>
        </form>
        {users.length === 0 && !loading && (
          <p className="text-sm text-muted-foreground py-4">Aún no hay usuarios.</p>
        )}
        {users.map((u) => (
          <div
            key={u.id}
            className="flex flex-wrap items-center justify-between gap-3 py-3"
            data-testid={`admin-user-row-${u.email}`}
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-semibold text-sm truncate">{u.name || u.email.split("@")[0]}</p>
                <Badge
                  className={`rounded-md ${u.role === "admin" ? "bg-violet-100 text-violet-800 hover:bg-violet-100" : "bg-blue-100 text-blue-800 hover:bg-blue-100"}`}
                >
                  {u.role}
                </Badge>
                {u.has_pending_reset && (
                  <Badge className="rounded-md bg-amber-100 text-amber-800 hover:bg-amber-100 flex items-center gap-1">
                    <ShieldAlert className="h-3 w-3" /> reset solicitado
                  </Badge>
                )}
                {u.temp_password && (
                  <Badge className="rounded-md bg-amber-100 text-amber-800 hover:bg-amber-100">temporal</Badge>
                )}
                {!u.temp_password && !u.protected && (
                  <Badge className="rounded-md bg-emerald-100 text-emerald-800 hover:bg-emerald-100 flex items-center gap-1">
                    <ShieldCheck className="h-3 w-3" /> personalizada
                  </Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground truncate">{u.email}</p>
              {u.temp_password && (
                <button
                  type="button"
                  onClick={() => copy(u.temp_password)}
                  data-testid={`admin-temp-password-${u.email}`}
                  className="mt-1 inline-flex items-center gap-1 text-xs font-mono bg-muted px-2 py-0.5 rounded hover:bg-muted/80"
                  title="Copiar"
                >
                  {u.temp_password} <Copy className="h-3 w-3" />
                </button>
              )}
            </div>
            {!u.protected && (
              <div className="flex items-center gap-2 flex-wrap">
                <Input
                  data-testid={`admin-custom-password-input-${u.email}`}
                  value={custom[u.id] || ""}
                  onChange={(e) => setCustom((c) => ({ ...c, [u.id]: e.target.value }))}
                  placeholder="Contraseña personalizada (mín. 8)"
                  className="h-8 w-56 rounded-md text-xs font-mono"
                  autoComplete="off"
                />
                <Button
                  data-testid={`admin-set-password-button-${u.email}`}
                  variant="default"
                  size="sm"
                  onClick={() => reset(u, (custom[u.id] || "").trim())}
                  disabled={resetting === u.id || (custom[u.id] || "").trim().length < 8}
                  className="rounded-md"
                >
                  <span className="flex items-center gap-1"><KeyRound className="h-3 w-3" /> fijar</span>
                </Button>
                <Button
                  data-testid={`admin-reset-button-${u.email}`}
                  variant="outline"
                  size="sm"
                  onClick={() => reset(u)}
                  disabled={resetting === u.id}
                  className="rounded-md"
                >
                  {resetting === u.id ? (
                    <span className="flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> generando…</span>
                  ) : (
                    <span className="flex items-center gap-1"><RefreshCw className="h-3 w-3" /> nueva temporal</span>
                  )}
                </Button>
              </div>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
    </div>
  );
}
